import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import StatCard from '../components/StatCard';
import { api, fmtEUR } from '../lib/api';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart } from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 2 + i);

export default function Tresorerie() {
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/dashboard/tresorerie?annee=${annee}`);
      setData(data);
    })();
  }, [annee]);

  if (!data) return <Layout title="Trésorerie"><div>Chargement...</div></Layout>;

  return (
    <Layout
      title="Trésorerie"
      subtitle="Fonds de roulement & besoins"
      actions={
        <Select value={String(annee)} onValueChange={(v) => setAnnee(Number(v))}>
          <SelectTrigger className="w-32 rounded-none border-zinc-900" data-testid="tresorerie-annee"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-none">
            {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <StatCard testid="tres-fonds-initial" label="Fonds initial" value={data.fonds_roulement_initial} accent />
        <StatCard testid="tres-solde-fin" label="Solde fin d'année" value={data.solde_fin} />
        <StatCard testid="tres-besoin-max" label="Besoin max trésorerie" value={data.besoin_max_tresorerie} />
        <StatCard testid="tres-total-entrees" label="Total entrées" value={data.data.reduce((s, d) => s + d.entrees, 0)} />
      </div>

      <div className="bg-white border border-zinc-200 p-6 mb-6">
        <h3 className="font-display font-bold text-xl tracking-tight mb-4">Évolution du solde</h3>
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={data.data}>
            <CartesianGrid strokeDasharray="0" stroke="#e4e4e7" vertical={false} />
            <XAxis dataKey="mois" tick={{ fontSize: 11, fontFamily: 'IBM Plex Mono' }} stroke="#71717a" />
            <YAxis tick={{ fontSize: 10, fontFamily: 'IBM Plex Mono' }} stroke="#71717a" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
            <Tooltip contentStyle={{ borderRadius: 0, border: '1px solid #18181b', fontFamily: 'IBM Plex Mono', fontSize: 12 }} formatter={(v) => fmtEUR(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="linear" dataKey="solde" stroke="#002FA7" strokeWidth={2.5} name="Solde" dot={{ r: 3 }} />
            <Line type="linear" dataKey="besoin_tresorerie" stroke="#E11D48" strokeWidth={2} name="Besoin" dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white border border-zinc-200 p-6 mb-6">
        <h3 className="font-display font-bold text-xl tracking-tight mb-4">Flux mensuels</h3>
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={data.data}>
            <CartesianGrid strokeDasharray="0" stroke="#e4e4e7" vertical={false} />
            <XAxis dataKey="mois" tick={{ fontSize: 11, fontFamily: 'IBM Plex Mono' }} stroke="#71717a" />
            <YAxis tick={{ fontSize: 10, fontFamily: 'IBM Plex Mono' }} stroke="#71717a" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
            <Tooltip contentStyle={{ borderRadius: 0, border: '1px solid #18181b', fontFamily: 'IBM Plex Mono', fontSize: 12 }} formatter={(v) => fmtEUR(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="entrees" fill="#10B981" name="Entrées" />
            <Bar dataKey="sorties" fill="#E11D48" name="Sorties" />
            <Bar dataKey="assurance" fill="#F59E0B" name="Assurance" />
            <Line type="linear" dataKey="flux" stroke="#002FA7" strokeWidth={2} name="Flux net" dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="tresorerie-table">
        <div className="border-b border-zinc-900 px-6 py-3">
          <h3 className="font-display font-bold text-lg tracking-tight">Détail mensuel</h3>
        </div>
        <table className="w-full text-sm">
          <thead className="border-b border-zinc-200">
            <tr className="text-left">
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Mois</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium text-right">Entrées</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium text-right">Sorties</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium text-right">Assurance</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium text-right">Flux net</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium text-right">Solde</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium text-right">Besoin</th>
            </tr>
          </thead>
          <tbody>
            {data.data.map((d) => (
              <tr key={d.mois_num} className="border-b border-zinc-100 hover:bg-zinc-50">
                <td className="px-4 py-2 font-medium">{d.mois}</td>
                <td className="px-4 py-2 font-mono text-right text-emerald-700">{fmtEUR(d.entrees)}</td>
                <td className="px-4 py-2 font-mono text-right text-rose-600">{fmtEUR(d.sorties)}</td>
                <td className="px-4 py-2 font-mono text-right text-amber-700">{fmtEUR(d.assurance)}</td>
                <td className={`px-4 py-2 font-mono text-right font-medium ${d.flux >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>{fmtEUR(d.flux)}</td>
                <td className={`px-4 py-2 font-mono text-right font-medium ${d.solde >= 0 ? 'text-zinc-900' : 'text-rose-600'}`}>{fmtEUR(d.solde)}</td>
                <td className="px-4 py-2 font-mono text-right text-rose-600">{d.besoin_tresorerie > 0 ? fmtEUR(d.besoin_tresorerie) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
